import { forwardRef, InputHTMLAttributes, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
  leadingIcon?: ReactNode;
}

export const TextField = forwardRef<HTMLInputElement, FieldProps>(function TextField(
  { label, hint, error, leadingIcon, className, id, ...rest },
  ref,
) {
  const inputId = id || `f-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="space-y-1.5">
      <label htmlFor={inputId} className="block text-sm font-medium text-ink-800">
        {label}
      </label>
      <div className="relative">
        {leadingIcon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500">{leadingIcon}</span>
        )}
        <input
          id={inputId}
          ref={ref}
          {...rest}
          className={`w-full h-11 rounded-xl border bg-white px-3 ${leadingIcon ? 'pl-10' : ''} text-sm transition outline-none placeholder:text-ink-400 focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500 ${
            error ? 'border-warn-500/60' : 'border-ink-200'
          } ${className || ''}`}
        />
      </div>
      {error ? (
        <p className="flex items-center gap-1.5 text-xs text-warn-600">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      ) : (
        hint && <p className="text-xs text-ink-500">{hint}</p>
      )}
    </div>
  );
});

interface PasswordFieldProps extends FieldProps {
  showStrength?: boolean;
}

export function PasswordField({ showStrength, ...rest }: PasswordFieldProps) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);
  return (
    <div className="space-y-1.5">
      <label htmlFor={rest.id || 'password'} className="block text-sm font-medium text-ink-800">
        {rest.label}
      </label>
      <div className="relative">
        {rest.leadingIcon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500">{rest.leadingIcon}</span>
        )}
        <input
          id={rest.id || 'password'}
          name={rest.name}
          value={rest.value}
          onChange={rest.onChange}
          onBlur={rest.onBlur}
          autoComplete={rest.autoComplete}
          required={rest.required}
          placeholder={rest.placeholder}
          type={shown ? 'text' : 'password'}
          className={`w-full h-11 rounded-xl border bg-white px-3 ${rest.leadingIcon ? 'pl-10' : ''} pr-10 text-sm transition outline-none placeholder:text-ink-400 focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500 ${
            rest.error ? 'border-warn-500/60' : 'border-ink-200'
          }`}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShown((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-900"
          aria-label={shown ? t('auth.hide_password') : t('auth.show_password')}
        >
          {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>

      {showStrength && typeof rest.value === 'string' && rest.value.length > 0 && (
        <PasswordStrength value={rest.value} />
      )}

      {rest.error ? (
        <p className="flex items-center gap-1.5 text-xs text-warn-600">
          <AlertCircle className="h-3.5 w-3.5" />
          {rest.error}
        </p>
      ) : (
        rest.hint && <p className="text-xs text-ink-500">{rest.hint}</p>
      )}
    </div>
  );
}

/**
 * Heuristic password-strength meter. Not crypto-grade — just a UX
 * nudge to encourage longer, more varied passwords. Score 0-4.
 */
function scorePassword(v: string): number {
  let s = 0;
  if (v.length >= 8) s++;
  if (v.length >= 12) s++;
  if (/[A-Z]/.test(v) && /[a-z]/.test(v)) s++;
  if (/\d/.test(v)) s++;
  if (/[^A-Za-z0-9]/.test(v)) s++;
  return Math.min(s, 4);
}

function PasswordStrength({ value }: { value: string }) {
  const { t } = useTranslation();
  const s = scorePassword(value);
  const labels = [
    t('auth.pw_too_short'),
    t('auth.pw_weak'),
    t('auth.pw_ok'),
    t('auth.pw_strong'),
    t('auth.pw_very_strong'),
  ];
  const colors = ['bg-warn-500', 'bg-warn-500', 'bg-amber-500', 'bg-emerald-500', 'bg-emerald-600'];
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 grid grid-cols-4 gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`h-1 rounded-full transition ${i < s ? colors[s] : 'bg-ink-200'}`}
          />
        ))}
      </div>
      <span className="text-[10px] uppercase tracking-wider text-ink-500">{labels[s]}</span>
    </div>
  );
}

export function OrDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-ink-200" />
      <span className="text-[10px] uppercase tracking-widest text-ink-500 whitespace-nowrap">
        {label}
      </span>
      <div className="h-px flex-1 bg-ink-200" />
    </div>
  );
}

export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div role="alert" className="rounded-xl border border-warn-500/40 bg-warn-50 px-3 py-2.5 text-sm text-warn-600">
      <div className="flex items-start gap-2">
        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>{message}</span>
      </div>
    </div>
  );
}

export function SubmitButton({
  loading,
  children,
  disabled,
}: {
  loading?: boolean;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="submit"
      disabled={disabled || loading}
      className="gradient-primary inline-flex w-full h-11 items-center justify-center rounded-xl text-white text-sm font-semibold shadow-glow hover:shadow-glow-pink hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:pointer-events-none transition"
    >
      {loading ? (
        <span className="inline-flex items-center gap-2">
          <span className="h-3.5 w-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
          {children}
        </span>
      ) : (
        children
      )}
    </button>
  );
}
